/** Parse KP metadata.coachOverlay / learnerEvidence (same schema as Factory unit.meta). */

export type OverlayAnnotationDisplay = {
  id: string;
  inkRole: string;
  kind: string;
  label: string;
  text: string;
  boxLabel: string;
};

export type CoachOverlayDisplay = {
  summaryLabel: string;
  version: string;
  model: string;
  admonitions: Array<{ text: string; annotationIds: string[] }>;
  traps: Array<{ title: string; wrongHint: string; rightHint: string; annotationIds: string[] }>;
  strategyTags: string[];
  identityBank: Array<{ expr: string; annotationIds: string[] }>;
  methodVariants: Array<{ problemRef: string; methods: string[] }>;
  annotations: OverlayAnnotationDisplay[];
  hasContent: boolean;
};

export type LearnerOverlayDisplay = {
  summaryLabel: string;
  version: string;
  model: string;
  struggleSignals: Array<{ kind: string; summary: string; annotationIds: string[] }>;
  scratchPaths: Array<{ summary: string; annotationIds: string[] }>;
  annotations: OverlayAnnotationDisplay[];
  hasContent: boolean;
};

export type OverlayLayerKind = 'coach' | 'learner';

function readOverlayStatus(metadata: Record<string, unknown> | null | undefined, layer: OverlayLayerKind): string {
  if (!metadata) return '';
  const raw = layer === 'coach' ? metadata.coachOverlayStatus : metadata.learnerEvidenceStatus;
  return String(raw || '').trim().toLowerCase();
}

export function overlayLayerUnsettled(status: unknown): boolean {
  const value = String(status || '').trim().toLowerCase();
  return value === 'running' || value === 'pending';
}

/** 副券/详情在提取中应转圈：本层 running/pending，或本层仍空且正在 kickoff。 */
export function overlayLayerIsExtracting(input: {
  layer: OverlayLayerKind;
  metadata?: Record<string, unknown> | null;
  extracting?: boolean;
  hasContent?: boolean;
}): boolean {
  if (input.hasContent) {
    return false;
  }
  const status = readOverlayStatus(input.metadata, input.layer);
  if (overlayLayerUnsettled(status)) {
    return true;
  }
  if (status === 'failed' || status === 'succeeded') {
    return false;
  }
  return Boolean(input.extracting);
}

export function overlayLayerErrorMessage(
  metadata: Record<string, unknown> | null | undefined,
  layer: OverlayLayerKind,
): string | null {
  if (!metadata) return null;
  const raw = layer === 'coach' ? metadata.coachOverlayError : metadata.learnerEvidenceError;
  const text = String(raw || '').trim();
  return text || null;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function str(value: unknown, max = 400): string {
  const s = String(value ?? '').trim();
  return s.length > max ? `${s.slice(0, max)}…` : s;
}

export function overlayKindLabel(kind: string): string {
  const key = String(kind || '').trim();
  if (!key) return '';
  const map: Record<string, string> = {
    strike_rewrite: '划改重写',
    side_check: '旁路验算',
    multi_attempt: '多次尝试',
    diagram_draft: '草图草稿',
    other: '其它',
    admonition: '嘱咐',
    trap: '陷阱',
    strategy: '策略',
    identity: '恒等式',
    method: '手法',
    annotation: '标注',
    note: '旁注',
    box: '框选',
    circle: '圈注',
    underline: '下划',
    arrow: '指向',
  };
  return map[key] || map[key.toLowerCase()] || key.replace(/_/g, ' ');
}

export function overlayInkRoleLabel(role: string): string {
  const key = String(role || '').trim().toLowerCase();
  if (!key) return '';
  const map: Record<string, string> = {
    printed: '印刷',
    coach: '教练笔',
    learner: '学情笔',
    unknown: '未判',
  };
  return map[key] || role;
}

function parseAnnotation(raw: unknown, index: number): OverlayAnnotationDisplay | null {
  const row = asRecord(raw);
  if (!row) return null;
  const box = asRecord(row.box);
  const boxLabel = box
    ? `x=${Number(box.x).toFixed(2)} y=${Number(box.y).toFixed(2)} w=${Number(box.w).toFixed(2)} h=${Number(box.h).toFixed(2)}`
    : '';
  return {
    id: str(row.id || `a${index + 1}`, 40),
    inkRole: str(row.inkRole, 24),
    kind: str(row.kind, 40),
    label: str(row.label, 80),
    text: str(row.text, 240),
    boxLabel,
  };
}

function parseAnnotations(raw: unknown): OverlayAnnotationDisplay[] {
  return asArray(raw)
    .map((item, i) => parseAnnotation(item, i))
    .filter((x): x is OverlayAnnotationDisplay => Boolean(x));
}

export function readParticleCoachOverlayRecord(
  metadata?: Record<string, unknown> | null,
): Record<string, unknown> | null {
  if (!metadata) return null;
  return asRecord(metadata.coachOverlay) || asRecord(metadata.coach_overlay);
}

export function readParticleLearnerEvidenceRecord(
  metadata?: Record<string, unknown> | null,
): Record<string, unknown> | null {
  if (!metadata) return null;
  return asRecord(metadata.learnerEvidence) || asRecord(metadata.learner_evidence);
}

export function buildCoachOverlayDisplay(
  overlay: Record<string, unknown> | null | undefined,
): CoachOverlayDisplay | null {
  if (!overlay) return null;
  const version = str(overlay.version, 40);
  const model = str(overlay.model, 60);
  const admonitions = asArray(overlay.admonitions).map((item) => {
    const row = asRecord(item);
    return {
      text: str(row?.text, 200),
      annotationIds: asArray(row?.annotationIds).map((id) => str(id, 40)).filter(Boolean),
    };
  }).filter((x) => x.text);
  const traps = asArray(overlay.traps).map((item) => {
    const row = asRecord(item);
    return {
      title: str(row?.title, 80),
      wrongHint: str(row?.wrongHint, 200),
      rightHint: str(row?.rightHint, 200),
      annotationIds: asArray(row?.annotationIds).map((id) => str(id, 40)).filter(Boolean),
    };
  }).filter((x) => x.title || x.wrongHint || x.rightHint);
  const strategyTags = asArray(overlay.strategyTags).map((t) => str(t, 40)).filter(Boolean);
  const identityBank = asArray(overlay.identityBank).map((item) => {
    const row = asRecord(item);
    return {
      expr: str(row?.expr, 120),
      annotationIds: asArray(row?.annotationIds).map((id) => str(id, 40)).filter(Boolean),
    };
  }).filter((x) => x.expr);
  const methodVariants = asArray(overlay.methodVariants).map((item) => {
    const row = asRecord(item);
    return {
      problemRef: str(row?.problemRef, 40),
      methods: asArray(row?.methods).map((m) => str(m, 120)).filter(Boolean),
    };
  }).filter((x) => x.problemRef || x.methods.length);
  const annotations = parseAnnotations(overlay.annotations);
  const hasContent = Boolean(
    admonitions.length
    || traps.length
    || strategyTags.length
    || identityBank.length
    || methodVariants.length
    || annotations.length,
  );
  const bits = [
    annotations.length ? `标注 ${annotations.length}` : '',
    traps.length ? `陷阱 ${traps.length}` : '',
    admonitions.length ? `嘱咐 ${admonitions.length}` : '',
    strategyTags.length ? `策略 ${strategyTags.length}` : '',
  ].filter(Boolean);
  return {
    summaryLabel: bits.length ? `教练层 · ${bits.join(' · ')}` : '教练层',
    version,
    model,
    admonitions,
    traps,
    strategyTags,
    identityBank,
    methodVariants,
    annotations,
    hasContent,
  };
}

export function buildLearnerOverlayDisplay(
  evidence: Record<string, unknown> | null | undefined,
): LearnerOverlayDisplay | null {
  if (!evidence) return null;
  const version = str(evidence.version, 40);
  const model = str(evidence.model, 60);
  const struggleSignals = asArray(evidence.struggleSignals).map((item) => {
    const row = asRecord(item);
    return {
      kind: str(row?.kind, 40),
      summary: str(row?.summary, 200),
      annotationIds: asArray(row?.annotationIds).map((id) => str(id, 40)).filter(Boolean),
    };
  }).filter((x) => x.summary || x.kind);
  const scratchPaths = asArray(evidence.scratchPaths).map((item) => {
    const row = asRecord(item);
    return {
      summary: str(row?.summary, 200),
      annotationIds: asArray(row?.annotationIds).map((id) => str(id, 40)).filter(Boolean),
    };
  }).filter((x) => x.summary);
  const annotations = parseAnnotations(evidence.annotations);
  const hasContent = Boolean(
    struggleSignals.length || scratchPaths.length || annotations.length,
  );
  const bits = [
    annotations.length ? `标注 ${annotations.length}` : '',
    struggleSignals.length ? `挣扎 ${struggleSignals.length}` : '',
    scratchPaths.length ? `草稿 ${scratchPaths.length}` : '',
  ].filter(Boolean);
  return {
    summaryLabel: bits.length ? `学习层 · ${bits.join(' · ')}` : '学习层',
    version,
    model,
    struggleSignals,
    scratchPaths,
    annotations,
    hasContent,
  };
}

export function resolveParticleCoachOverlayDisplay(
  metadata?: Record<string, unknown> | null,
): CoachOverlayDisplay | null {
  return buildCoachOverlayDisplay(readParticleCoachOverlayRecord(metadata));
}

export function resolveParticleLearnerOverlayDisplay(
  metadata?: Record<string, unknown> | null,
): LearnerOverlayDisplay | null {
  return buildLearnerOverlayDisplay(readParticleLearnerEvidenceRecord(metadata));
}

export type OverlayStubLine = {
  key: string;
  text: string;
};

export function buildCoachOverlayStubLines(
  display: CoachOverlayDisplay | null | undefined,
  maxLines = 6,
): OverlayStubLine[] {
  if (!display?.hasContent) return [];
  const lines: OverlayStubLine[] = [];
  for (const item of display.admonitions) {
    if (lines.length >= maxLines) break;
    lines.push({ key: `adm-${lines.length}`, text: item.text });
  }
  for (const trap of display.traps) {
    if (lines.length >= maxLines) break;
    lines.push({ key: `trap-${lines.length}`, text: trap.title || trap.wrongHint || trap.rightHint });
  }
  for (const tag of display.strategyTags) {
    if (lines.length >= maxLines) break;
    lines.push({ key: `stg-${lines.length}`, text: tag });
  }
  for (const item of display.identityBank) {
    if (lines.length >= maxLines) break;
    lines.push({ key: `id-${lines.length}`, text: item.expr });
  }
  for (const ann of display.annotations) {
    if (lines.length >= maxLines) break;
    const title = ann.label || overlayKindLabel(ann.kind) || ann.text;
    if (title) lines.push({ key: `ann-${lines.length}`, text: title });
  }
  return lines;
}

export function buildLearnerOverlayStubLines(
  display: LearnerOverlayDisplay | null | undefined,
  maxLines = 6,
): OverlayStubLine[] {
  if (!display?.hasContent) return [];
  const lines: OverlayStubLine[] = [];
  for (const item of display.struggleSignals) {
    if (lines.length >= maxLines) break;
    lines.push({
      key: `sg-${lines.length}`,
      text: item.summary || overlayKindLabel(item.kind) || item.kind,
    });
  }
  for (const item of display.scratchPaths) {
    if (lines.length >= maxLines) break;
    lines.push({ key: `sp-${lines.length}`, text: item.summary });
  }
  for (const ann of display.annotations) {
    if (lines.length >= maxLines) break;
    const title = ann.label || overlayKindLabel(ann.kind) || ann.text;
    if (title) lines.push({ key: `ann-${lines.length}`, text: title });
  }
  return lines;
}
